import React, { FC } from "react";

import { IgHeaderWTooltip, IgPaper, IgTypography } from "@intelligems/ig-theme";
import { ExperienceDto } from "@intelligems/ig-types";
import { Grid, SvgIcon } from "@mui/material";

import { AnalysisLegendIcon } from "../../../../images/Icons/AnalysisLegendIcon";
import theme from "../../../../theme";
import { ImpactMetricData } from "../../../../types/analytics";
import { ImpactBarGraph } from "../../../Dashboard/Graphs/ImpactBarGraph";

import { CopiedImpactBarGraph } from "./CopiedImpactBarGraph";

interface GroupAnalysisProps {
  controlWinners: ExperienceDto[];
  nonControlWinners: ExperienceDto[];
  allImpactExperiments: ExperienceDto[];
  impactMetric: string;
}

export const GroupAnalysis: FC<GroupAnalysisProps> = ({
  nonControlWinners,
  controlWinners,
  allImpactExperiments,
  impactMetric,
}) => {
  const formatData = (): ImpactMetricData[] => {
    return allImpactExperiments.map((data: ExperienceDto) => {
      const isControlWinner = data.variations.find(
        (group) => group.id === data.experienceAnalysis.impactWinnerVariationId
      )?.isControl;

      return {
        name: data.name,
        value: data.experienceAnalysis.impact!,
        isControlWinner: isControlWinner!,
      };
    });
  };
  return (
    <IgPaper
      sx={{
        boxShadow: "none",
        padding: "20px 40px 20px 40px",
      }}
    >
      <Grid item container direction={"column"}>
        <Grid item sx={{ mb: 4 }}>
          <IgHeaderWTooltip
            sectionTitle={"Analysis of Winners"}
            tooltipMessage={
              "Estimated monthly impact for five most recent tests. Calculated based on winning group vs. control (uplift) or, in the case where control was the winning group, control vs. the second-highest performing group (loss avoidance)"
            }
          />
        </Grid>
        <Grid item container direction={"row"} justifyContent={"space-between"}>
          <Grid item container direction={"column"} xs={12} lg={3}>
            <IgTypography
              sx={{
                fontSize: "22px",
                color: theme.palette.primary.dark,
                fontWeight: 600,
                marginBottom: "10px",
              }}
            >
              {nonControlWinners.length} test
              {nonControlWinners.length < 1 || nonControlWinners.length > 1
                ? "s have"
                : " has"}{" "}
              led to direct uplift{nonControlWinners.length > 0 && "!"}
            </IgTypography>
            <IgTypography
              sx={{
                fontSize: "22px",
                color: "#909090",
                fontWeight: 600,
              }}
            >
              {controlWinners.length} test
              {controlWinners.length > 1 || controlWinners.length < 1
                ? "s have"
                : " has"}{" "}
              found control as the winning test group.
            </IgTypography>
          </Grid>
          <Grid item xs={12} lg={8}>
            {allImpactExperiments.length <= 1 ? (
              <CopiedImpactBarGraph
                experience={allImpactExperiments[0]}
                impactMetric={impactMetric}
              />
            ) : (
              <ImpactBarGraph
                impactData={formatData()?.slice(0, 5)}
                impactMetric={impactMetric}
              />
            )}
          </Grid>
        </Grid>
        {allImpactExperiments.length > 1 && (
          <Grid item container direction={"row"} alignItems={"center"}>
            <SvgIcon
              component={AnalysisLegendIcon}
              inheritViewBox={true}
              sx={{ color: theme.palette.primary.dark, fontSize: "14px" }}
            />
            <IgTypography
              sx={{
                fontSize: "15px",
                color: theme.palette.gray.main,
                marginTop: "-8px",
                marginRight: "15px",
              }}
            >
              Uplift
            </IgTypography>

            <SvgIcon
              component={AnalysisLegendIcon}
              inheritViewBox={true}
              sx={{ color: theme.palette.disabled.main, fontSize: "14px" }}
            />
            <IgTypography
              sx={{
                fontSize: "15px",
                marginTop: "-8px",
                color: theme.palette.gray.main,
              }}
            >
              Loss Avoidance
            </IgTypography>
          </Grid>
        )}
      </Grid>
    </IgPaper>
  );
};
